// src/utils/toast.js
import React, { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
  X,
  Loader2,
} from "lucide-react";

// Toast 타입별 설정
const toastConfig = {
  success: {
    icon: CheckCircle,
    className: "bg-green-50 border-green-200 text-green-900",
    iconClassName: "text-green-500",
    progressClassName: "bg-green-500",
  },
  error: {
    icon: XCircle,
    className: "bg-red-50 border-red-200 text-red-900",
    iconClassName: "text-red-500",
    progressClassName: "bg-red-500",
  },
  warning: {
    icon: AlertCircle,
    className: "bg-yellow-50 border-yellow-200 text-yellow-900",
    iconClassName: "text-yellow-500",
    progressClassName: "bg-yellow-500",
  },
  info: {
    icon: Info,
    className: "bg-blue-50 border-blue-200 text-blue-900",
    iconClassName: "text-blue-500",
    progressClassName: "bg-blue-500",
  },
  loading: {
    icon: Loader2,
    className: "bg-gray-50 border-gray-200 text-gray-900",
    iconClassName: "text-gray-500 animate-spin",
    progressClassName: "bg-gray-500",
  },
};

// Toast 컴포넌트
function ToastItem({ id, type, title, message, duration, onClose }) {
  const [isLeaving, setIsLeaving] = useState(false);
  const [progress, setProgress] = useState(100);
  const config = toastConfig[type] || toastConfig.info;
  const Icon = config.icon;

  useEffect(() => {
    if (duration && type !== "loading") {
      const interval = setInterval(() => {
        setProgress((prev) => {
          const next = prev - 100 / (duration / 100);
          if (next <= 0) {
            handleClose();
            return 0;
          }
          return next;
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [duration, type]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => onClose(id), 300);
  };

  return (
    <div
      className={`
        relative overflow-hidden min-w-[320px] max-w-md
        border rounded-lg shadow-lg
        transform transition-all duration-300 ease-in-out
        ${config.className}
        ${
          isLeaving ? "translate-x-full opacity-0" : "translate-x-0 opacity-100"
        }
        animate-slide-in-right
      `}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <Icon
            className={`w-5 h-5 flex-shrink-0 mt-0.5 ${config.iconClassName}`}
          />
          <div className="flex-1 min-w-0">
            {title && <p className="font-semibold text-sm mb-1">{title}</p>}
            {message && <p className="text-sm opacity-90">{message}</p>}
          </div>
          {type !== "loading" && (
            <button
              onClick={handleClose}
              className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {duration && type !== "loading" && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10">
          <div
            className={`h-full transition-all duration-100 ease-linear ${config.progressClassName}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

// Toast Container 컴포넌트
function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const handleToast = (event) => {
      const {
        type,
        title,
        message,
        duration = 4000,
        id = Date.now(),
      } = event.detail;
      setToasts((prev) => [...prev, { id, type, title, message, duration }]);
    };

    window.addEventListener("show-toast", handleToast);
    return () => window.removeEventListener("show-toast", handleToast);
  }, []);

  const handleClose = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} {...toast} onClose={handleClose} />
      ))}
    </div>
  );
}

// Toast 시스템 초기화
let toastRoot = null;

export function initToast() {
  const containerId = "toast-container-root";
  let container = document.getElementById(containerId);

  if (!container) {
    container = document.createElement("div");
    container.id = containerId;
    document.body.appendChild(container);
    toastRoot = createRoot(container);
    toastRoot.render(<ToastContainer />);
  }
}

// Toast 표시 함수들
export function toast(options) {
  if (!toastRoot) {
    initToast();
  }

  const event = new CustomEvent("show-toast", {
    detail: typeof options === "string" ? { message: options } : options,
  });
  window.dispatchEvent(event);

  return options.id || Date.now();
}

// 편의 함수들
toast.success = (title, message) => {
  if (typeof title === "string" && !message) {
    return toast({ type: "success", message: title });
  }
  return toast({ type: "success", title, message });
};

toast.error = (title, message) => {
  if (typeof title === "string" && !message) {
    return toast({ type: "error", message: title });
  }
  return toast({ type: "error", title, message });
};

toast.warning = (title, message) => {
  if (typeof title === "string" && !message) {
    return toast({ type: "warning", message: title });
  }
  return toast({ type: "warning", title, message });
};

toast.info = (title, message) => {
  if (typeof title === "string" && !message) {
    return toast({ type: "info", message: title });
  }
  return toast({ type: "info", title, message });
};

toast.loading = (message = "처리중...") => {
  return toast({ type: "loading", message, duration: 0 });
};

toast.dismiss = (id) => {
  const event = new CustomEvent("dismiss-toast", { detail: { id } });
  window.dispatchEvent(event);
};

// Promise 기반 토스트
toast.promise = (promise, { loading, success, error }) => {
  const id = toast.loading(loading);

  promise
    .then((result) => {
      toast.dismiss(id);
      if (typeof success === "function") {
        const msg = success(result);
        toast.success(msg);
      } else {
        toast.success(success);
      }
      return result;
    })
    .catch((err) => {
      toast.dismiss(id);
      if (typeof error === "function") {
        const msg = error(err);
        toast.error(msg);
      } else {
        toast.error(error);
      }
      throw err;
    });

  return promise;
};

// CSS 애니메이션 (Tailwind 클래스로 처리 안되는 경우를 위해)
const style = document.createElement("style");
style.textContent = `
  @keyframes slide-in-right {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  .animate-slide-in-right {
    animation: slide-in-right 0.3s ease-out;
  }
  
  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  .animate-fade-in {
    animation: fade-in 0.2s ease-out;
  }
`;
document.head.appendChild(style);

// 자동 초기화
if (typeof window !== "undefined") {
  initToast();
}
